import {OffersBlock} from "@/lib/interfaces/blocks";
import BonusTickIcon from "@/components/icons/BonusTickIcon";
import SectionContent from "@/components/SectionContent";
import React, {Fragment, SyntheticEvent, useState} from "react";
import Image from "next/image";
import {Offer} from "@/lib/interfaces/offers";
import dynamic from "next/dynamic";
import "@/styles/offers.scss";
import {TrackingLink} from "@/components/TrackingLink";
import Link from "next/link";
import {removeHtmlTags} from "@/lib/helpers/string";
import BulbIcon from "@/components/icons/BulbIcon";
import SectionTitle from "@/components/SectionTitle";
import ReactGA from "react-ga4";
ReactGA.initialize(`${process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID}`);

const Table = dynamic(
    () => import("@/components/Table"),
    { ssr: false }
);

const OfferDialogue = dynamic(
    () => import("@/components/blocks/offers/OfferDialogue"),
    { ssr: false }
);

interface OffersSectionProps {
    children: React.ReactNode
    type: string
}

function OffersSection({children, type}: OffersSectionProps) {
    return (
        <section className={`eo-sportsbook-list ${type}`}>
            {children}
        </section>
    )
}

type OffersType = {
    block: OffersBlock
}

function SportsbookLogo({offer, showFeatured = false}: {offer: Offer, showFeatured?: boolean}) {
    if (offer.brand.media.length) {
        return (
            <div className="sportsbook-logo">
                <div className="logo-wrap">
                    <div className="logo-box" style={{ backgroundColor: `${offer.brand.background_colour}` }}>
                        <Image width={150} height={75} className="logo-image" src={`${offer.brand.media[0]?.original_webp}, ${offer.brand.media[0].original_webp}, ${offer.brand.media[0].original_png}`} alt={`Logo for ${offer.brand.title}`}/>
                    </div>
                    {offer.featured && showFeatured ? <div className="logo-label" dangerouslySetInnerHTML={{__html: offer.brand.benefits[0].description}}></div> : null}
                </div>
            </div>
        )
    }
}

function SportsbookOfferTitle({offer}: {offer: Offer}) {
    return (
        <span className="sportsbook-offerTitle">
            {offer.title}
        </span>
    )
}

function SportsbookPlay({offer, buttonText = "Play"}: {offer: Offer, buttonText?: string}) {
    const handleClick = () => {
        ReactGA.event({
            category: "Offer Click",
            action: "Offer Click",
            label: `${offer.brand.title} | ${offer.title}`,
        });
    };

    return (
        <div className="sportsbook-play">
            <TrackingLink source={"offer"}>
                <Link href={offer.url} target={"_blank"} onClick={handleClick} className="eo-button play-button" style={{justifyContent: 'center'}} aria-label={`Play with ${offer.brand.title}`}>{buttonText}</Link>
            </TrackingLink>
        </div>
    )
}

export default function Offers({ block }: OffersType) {
    const title = removeHtmlTags(block.content.title.en).trim();
    const [offerDialogueOpen, setOfferDialogueOpen] = useState(false);
    const [offer, setOffer] = useState<Offer>();
    const handleOfferDialogueOpen = (e: SyntheticEvent, browser: any) => {
        e.preventDefault();
        setOfferDialogueOpen(true);
        setOffer(browser);
    }
    switch (block.content.layout) {
        case "typ-offer":
            return (
                <OffersSection type={"m-offers"}>
                    <SectionTitle title={title}>
                        <div className="icon m-icon">
                            <BulbIcon/>
                        </div>
                    </SectionTitle>
                    {block.content.browsers.map((browser) => (
                        <div key={browser.id} className={`eo-sportsbook typ-offer ${browser.featured ? 'm-spotlight' : ''}`}>
                            <div className="sportsbook-backdrop"></div>
                            <SportsbookLogo offer={browser} showFeatured={true}/>
                            <SportsbookOfferTitle offer={browser}/>
                            <SportsbookPlay offer={browser} buttonText={"Play Now"}/>
                            <div className="sportsbook-more">
                                <div className="more-label">
                                </div>
                                <div className="more-links">
                                    {browser.brand.bonuses.length > 0 && 
                                    (<a href={`${browser.brand.bonuses[0].slug}`}>
                                        <BonusTickIcon/>
                                        <span>Bonuses</span>
                                    </a>)}
                                    {browser.brand.codes.length > 0 && 
                                    (<a href={`${browser.brand.codes[0].slug}`}>
                                        <BonusTickIcon/>
                                        <span>Codes</span>
                                    </a>)}
                                    {browser.brand.casino.length > 0 && 
                                    (<a href={`${browser.brand.casino[0].slug}`}>
                                        <BonusTickIcon/>
                                        <span>Casino</span>
                                    </a>)}
                                </div>
                            </div>
                            <div className="sportsbook-terms">
                                <div dangerouslySetInnerHTML={{ __html: browser?.terms || "Terms & Conditions Apply, 18+" }} />
                            </div>
                        </div>
                    ))}
                </OffersSection>
            )
        case "typ-basic":
            return (
                <>
                    <SectionTitle title={title}>
                            <div className="icon m-icon">
                                <BulbIcon/>
                            </div>
                        </SectionTitle>
                    <OffersSection type={"m-basic"}>
                        {block.content.browsers.map((browser) => (
                            <div key={browser.id} className="eo-sportsbook typ-basic ">
                                <SportsbookLogo offer={browser}/>
                                <div className="sportsbook-play">
                                    <TrackingLink source={"offer"}>
                                        <a onClick={(e) => handleOfferDialogueOpen(e, browser)} className="eo-button play-button" style={{justifyContent: 'center', cursor: "pointer"}} aria-label={`Play with ${browser.brand.title}`}>Play Now</a>
                                    </TrackingLink>
                                </div>
                            </div>
                        ))}
                    </OffersSection>
                    {offerDialogueOpen && (
                        <OfferDialogue offer={offer} open={offerDialogueOpen} onClose={() => setOfferDialogueOpen(false)} />
                    )}
                </>
            )
        case "typ-table":
            const rows = block.content.browsers.map(offer => {
                return [
                    <>
                        <span className="mobileLabel">Sportsbook</span>
                        {offer.brand.title}
                    </>,
                    <>
                        <span className="mobileLabel">Offer</span>
                        {offer.title}
                        <div className="offer-terms" dangerouslySetInnerHTML={{__html: offer.terms ?? ''}}></div>
                    </>,
                    <>
                        <SportsbookPlay offer={offer}/>
                    </>
                ]
            })
            return (
                <Table className="m-mobileCards" columns={['Sportsbook', 'Offer', '']} rows={rows}/>
            )
        case "typ-betting":
        default:
            return (
                <OffersSection type={""}>
                    <SectionTitle title={title}>
                        <div className="icon m-icon">
                            <BulbIcon/>
                        </div>
                    </SectionTitle>
                    {block.content.browsers.map((browser) => (
                        <div key={browser.id} className="eo-sportsbook typ-betting ">
                            <SportsbookLogo offer={browser}/>
                            <SportsbookOfferTitle offer={browser}/>
                            <span className="sportsbook-benefit">
                                <svg viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M470.601 105.4C483.101 117.9 483.101 138.2 470.601 150.7L214.601 406.7C202.101 419.2 181.801 419.2 169.301 406.7L41.3008 278.7C28.8008 266.2 28.8008 245.9 41.3008 233.4C53.8008 220.9 74.1008 220.9 86.6008 233.4L192.001 338.7L425.401 105.4C437.901 92.9004 458.201 92.9004 470.701 105.4H470.601Z"></path>
                                </svg>
                                <span dangerouslySetInnerHTML={{__html: browser.brand.benefits[0].description}}></span>
                            </span>
                            <span className="sportsbook-terms">
                                <div dangerouslySetInnerHTML={{ __html: browser?.terms || "Terms & Conditions Apply, 18+" }} />
                            </span>
                            <SportsbookPlay offer={browser}/>
                        </div>
                    ))}
                </OffersSection>
            )
    }
}